import React, { useEffect, useMemo, useState } from "react";

import { auth, db } from "../firebase";

import {
  addDoc,
  collection,
  getDocs,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";

import "./AdminAddBaseball.css";

/**
 * Hutambua mtumiaji aliyeingia kwa Firebase Auth au hali ya login
 * iliyohifadhiwa na Login.jsx. Hii ni client-side check tu; Firestore
 * Security Rules bado zinapaswa kulinda ruhusa za admin.
 */
function getAppLoginUser() {
  if (auth.currentUser) {
    return {
      uid: auth.currentUser.uid,
      username: auth.currentUser.displayName || "",
      role: "firebase",
      source: "firebase",
    };
  }

  try {
    const stores = [window.localStorage, window.sessionStorage];
    let savedUser = {};
    let authenticated = false;
    let savedRole = "";

    for (const store of stores) {
      if (!store) continue;

      const authValue =
        store.getItem("authenticated") ||
        store.getItem("isAuthenticated") ||
        store.getItem("isLoggedIn");

      if (authValue === "true" || authValue === "1") {
        authenticated = true;
      }

      savedRole = savedRole || store.getItem("role") || "";

      const rawUser = store.getItem("user") || store.getItem("userData");
      if (rawUser && !Object.keys(savedUser).length) {
        try {
          const parsed = JSON.parse(rawUser);
          if (parsed && typeof parsed === "object") savedUser = parsed;
        } catch {
          // Ignore invalid stored JSON and continue checking other keys.
        }
      }
    }

    const username =
      savedUser.username ||
      savedUser.name ||
      window.localStorage.getItem("username") ||
      window.sessionStorage.getItem("username") ||
      "";

    const role = String(savedUser.role || savedRole || "").toLowerCase();

    // Do not accept a username alone as proof of login.
    if (!authenticated) return null;

    return {
      ...savedUser,
      uid: savedUser.uid || savedUser.id || username || "local-user",
      username,
      role,
      source: "storage",
    };
  } catch (error) {
    console.warn("Could not read app login state:", error);
    return null;
  }
}

const COLLECTION = "baseballmatches";
const TIMEZONE = "Africa/Dar_es_Salaam";

// Correct Score odds za msingi.
// Odds hizi zitatumika kama admin hajaweka odds sahihi.
const DEFAULT_SCORE_ODDS = [
  { score: "0 - 0", odds: 12 },
  { score: "1 - 0", odds: 9 },
  { score: "0 - 1", odds: 9 },
  { score: "1 - 1", odds: 7 },
  { score: "2 - 0", odds: 8 },
  { score: "0 - 2", odds: 8 },
  { score: "2 - 1", odds: 6.5 },
  { score: "1 - 2", odds: 6.5 },
  { score: "2 - 2", odds: 6 },
  { score: "3 - 0", odds: 7 },
  { score: "0 - 3", odds: 7 },
  { score: "3 - 1", odds: 5.5 },
  { score: "1 - 3", odds: 5.5 },
  { score: "3 - 2", odds: 5 },
  { score: "2 - 3", odds: 5 },
  { score: "4 - 2", odds: 6 },
  { score: "2 - 4", odds: 6 },
  { score: "4 - 3", odds: 7 },
  { score: "3 - 4", odds: 7 },
  { score: "5 - 2", odds: 8 },
  { score: "2 - 5", odds: 8 },
  { score: "5 - 3", odds: 9 },
  { score: "3 - 5", odds: 9 },
  { score: "4 - 4", odds: 10 },
  { score: "5 - 5", odds: 15 },
  { score: "Other Score", odds: 4 },
];

function cloneDefaultScoreOdds() {
  return DEFAULT_SCORE_ODDS.map((item) => ({
    score: item.score,
    odds: String(item.odds),
  }));
}

// Pakia Correct Score odds zilizohifadhiwa.
// Ikiwa odds hazipo au si sahihi, tumia default.
function normaliseScoreOdds(savedOdds) {
  const savedMap = new Map(
    (Array.isArray(savedOdds) ? savedOdds : []).map((item) => [
      item.score,
      item.odds,
    ])
  );

  return DEFAULT_SCORE_ODDS.map((item) => {
    const savedValue = Number(savedMap.get(item.score));

    return {
      score: item.score,
      odds:
        savedMap.has(item.score) &&
        Number.isFinite(savedValue) &&
        savedValue >= 1.01
          ? String(savedValue)
          : String(item.odds),
    };
  });
}

const EMPTY_FORM = {
  country: "Cuba",
  league: "Serie Nacional",
  homeTeam: "",
  awayTeam: "",
  date: "",
  time: "",
  homeOdds: "",
  awayOdds: "",
  totalLine: "10.5",
  overOdds: "",
  underOdds: "",
};

function clean(value) {
  return String(value ?? "")
    .replace(/<[^>]*>/g, "")
    .replace(/\*\*/g, "")
    .replace(/<\/?(strong|b)>/gi, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function numberOrNull(value) {
  if (value === "" || value == null) return null;

  const number = Number(String(value).replace(",", "."));

  return Number.isFinite(number) ? number : null;
}

function validDate(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) {
    return false;
  }

  const date = new Date(`${value}T12:00:00Z`);

  return (
    !Number.isNaN(date.getTime()) &&
    date.toISOString().slice(0, 10) === value
  );
}

function getToday(offset = 0) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());

  const year = Number(parts.find((part) => part.type === "year").value);
  const month = Number(parts.find((part) => part.type === "month").value);
  const day = Number(parts.find((part) => part.type === "day").value);

  return new Date(Date.UTC(year, month - 1, day + offset, 12))
    .toISOString()
    .slice(0, 10);
}

function parseDateTime(value) {
  const text = clean(value);

  const match = text.match(
    /\b(Today|Tomorrow|\d{1,2}\/\d{1,2}\/\d{2,4}|\d{4}-\d{1,2}-\d{1,2})\s+(\d{1,2}):(\d{2})(?:\s*(AM|PM))?/i
  );

  if (!match) return null;

  const datePart = match[1];
  let hour = Number(match[2]);
  const minute = Number(match[3]);
  const ampm = match[4]?.toUpperCase();

  if (minute > 59) return null;

  if (ampm) {
    if (hour < 1 || hour > 12) return null;

    if (ampm === "AM" && hour === 12) hour = 0;
    if (ampm === "PM" && hour !== 12) hour += 12;
  }

  if (hour > 23) return null;

  let date;

  if (/^today$/i.test(datePart)) {
    date = getToday();
  } else if (/^tomorrow$/i.test(datePart)) {
    date = getToday(1);
  } else if (datePart.includes("-")) {
    const [year, month, day] = datePart.split("-").map(Number);

    date = `${String(year).padStart(4, "0")}-${String(month).padStart(
      2,
      "0"
    )}-${String(day).padStart(2, "0")}`;
  } else {
    const [day, month, rawYear] = datePart.split("/").map(Number);
    const year = rawYear < 100 ? rawYear + 2000 : rawYear;

    date = `${String(year).padStart(4, "0")}-${String(month).padStart(
      2,
      "0"
    )}-${String(day).padStart(2, "0")}`;
  }

  if (!validDate(date)) return null;

  return {
    date,
    time: `${String(hour).padStart(2, "0")}:${String(minute).padStart(
      2,
      "0"
    )}`,
  };
}

// Hutambua odds zilizounganishwa kama 1.831.83,
// na kuzigeuza kuwa [1.83, 1.83].
function pricesIn(value) {
  return (
    String(value || "")
      .replace(/,/g, " ")
      .match(/\d+\.\d{2}/g) || []
  )
    .map(Number)
    .filter((number) => Number.isFinite(number) && number >= 1.01);
}

function parseLeague(value) {
  const text = clean(value);
  const parts = text.split(/\s+-\s+/);

  if (parts.length >= 2) {
    return {
      country: parts[0].trim(),
      league: parts.slice(1).join(" - ").trim(),
    };
  }

  return {
    country: "Other",
    league: text || "Baseball",
  };
}


function parseBaseballText(rawText) {
  const lines = String(rawText || "")
    .replace(/\r/g, "\n")
    .replace(/\u00a0/g, " ")
    .replace(/&nbsp;/gi, " ")
    .split("\n")
    .map((line) => clean(line))
    .filter(Boolean);

  const matches = [];

  let country = "USA";
  let league = "MLB";

  let teamNames = [];
  let oddsGroups = [];
  let totalLine = null;

  function resetGame() {
    teamNames = [];
    oddsGroups = [];
    totalLine = null;
  }

  function isMetadata(line) {
    return (
      /^(today|tomorrow)\b/i.test(line) ||
      /^under\s*over$/i.test(line) ||
      /^over\s*under$/i.test(line) ||
      /^underover$/i.test(line) ||
      /^\+\d+$/.test(line) ||
      /^\d+$/.test(line) ||
      /^mlb$/i.test(line) ||
      /^usa\s*-\s*mlb$/i.test(line)
    );
  }

  function extractOdds(line) {
    // Hushughulikia odds zilizoungana:
    // 2.031.67 1.672.03
    const found =
      String(line).match(/\d+\.\d{2}/g) || [];

    return found
      .map(Number)
      .filter((value) =>
        Number.isFinite(value) && value >= 1.01
      );
  }

  function finishGame(dateTime) {
    if (
      !dateTime ||
      teamNames.length < 2 ||
      !validDate(dateTime.date)
    ) {
      resetGame();
      return;
    }

    const homeTeam = clean(teamNames[0]);
    const awayTeam = clean(teamNames[1]);

    if (
      !homeTeam ||
      !awayTeam ||
      homeTeam.toLowerCase() === awayTeam.toLowerCase()
    ) {
      resetGame();
      return;
    }

    // Odds za kwanza mbili huhifadhiwa kama Match Winner.
    // Odds nyingine mbili huhifadhiwa kama Under/Over
    // pale ambapo bookmaker ameonyesha odds nne.
    const allOdds = oddsGroups.flat();

    const homeOdds = allOdds[0] ?? null;
    const awayOdds = allOdds[1] ?? null;

    const underOdds =
      allOdds.length >= 4 ? allOdds[2] : null;

    const overOdds =
      allOdds.length >= 4 ? allOdds[3] : null;

    matches.push({
      country,
      league,
      homeTeam,
      awayTeam,
      date: dateTime.date,
      time: dateTime.time,
      homeOdds,
      awayOdds,
      totalLine,
      underOdds,
      overOdds,
    });

    resetGame();
  }

  for (const line of lines) {
    // 1. Tambua nchi na ligi, mfano USA - MLB
    const leagueMatch = line.match(
      /^(.+?)\s+-\s+(.+)$/ 
    );

    if (leagueMatch && !/\d+\.\d{2}/.test(line)) {
      const possibleCountry = clean(leagueMatch[1]);
      const possibleLeague = clean(leagueMatch[2]);

      if (
        possibleCountry &&
        possibleLeague &&
        !/^\d+$/.test(possibleCountry)
      ) {
        country = possibleCountry;
        league = possibleLeague;
        continue;
      }
    }

    // 2. Tarehe na muda hufunga mechi inayokusanywa.
    // Mfano: Tomorrow 00:00#34629
    const dateTime = parseDateTime(line);

    if (dateTime) {
      finishGame(dateTime);
      continue;
    }

    // 3. Odds zinaweza kuwa zimeunganishwa bila nafasi.
    const prices = extractOdds(line);

    if (prices.length >= 2) {
      oddsGroups.push(prices);
      continue;
    }

    // 4. Market ya Total Runs inaweza kuwa na line kama 12.
    if (/^\d{1,2}(?:\.\d)?$/.test(line)) {
      const number = Number(line);

      if (number >= 0 && number <= 50) {
        totalLine = number;
      }

      continue;
    }

    // 5. Ruka maelezo ya bookmaker yasiyo majina ya timu.
    if (isMetadata(line)) continue;

    // 6. Ondoa ID au alama za ziada endapo zimeunganishwa
    // na jina la timu.
    const teamName = clean(
      line.replace(/\s*\+\d+\s*$/, "")
    );

    if (
      !teamName ||
      /\d{1,2}:\d{2}/.test(teamName) ||
      /^\d+(?:\.\d+)?$/.test(teamName)
    ) {
      continue;
    }

    // Tunahitaji majina mawili kwa kila mechi.
    if (teamNames.length < 2) {
      teamNames.push(teamName);
    }
  }

  return matches;
}

// ==============================
// SEHEMU YA 2: COMPONENT STATE
// NA FIREBASE FUNCTIONS
// ==============================

export default function AdminAddBaseball() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [form, setForm] = useState(() => ({
    ...EMPTY_FORM,
    scoreOdds: cloneDefaultScoreOdds(),
  }));

  const [selectedId, setSelectedId] = useState(null);

  const [bulkText, setBulkText] = useState("");
  const [bulkPreview, setBulkPreview] = useState([]);

  const [search, setSearch] = useState("");

  // ------------------------------
  // LOAD MATCHES FROM FIREBASE
  // ------------------------------
  const loadMatches = async () => {
    setLoading(true);
    setError("");

    try {
      const snapshot = await getDocs(collection(db, COLLECTION));

      const loadedMatches = snapshot.docs.map((item) => ({
        id: item.id,
        ...item.data(),
      }));

      loadedMatches.sort((a, b) => {
        const dateA = `${a.date || ""} ${a.time || ""}`;
        const dateB = `${b.date || ""} ${b.time || ""}`;

        return dateB.localeCompare(dateA);
      });

      setMatches(loadedMatches);
    } catch (err) {
      console.error("Error loading baseball matches:", err);
      setError("Imeshindikana kupakia mechi. Hakikisha Firebase imeunganishwa.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMatches();
  }, []);

  // ------------------------------
  // FORM HANDLERS
  // ------------------------------
  const changeForm = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const changeScoreOdd = (score, value) => {
    setForm((previous) => ({
      ...previous,
      scoreOdds: (previous.scoreOdds || cloneDefaultScoreOdds()).map(
        (item) =>
          item.score === score
            ? {
                ...item,
                odds: value,
              }
            : item
      ),
    }));
  };

  const resetForm = () => {
    setSelectedId(null);

    setForm({
      ...EMPTY_FORM,
      scoreOdds: cloneDefaultScoreOdds(),
    });

    setError("");
    setSuccess("");
  };

  // ------------------------------
  // SAFE CORRECT SCORE ODDS
  // ------------------------------
  // Odds za Correct Score hazitazuia kuhifadhi mechi.
  // Ikiwa odds imeachwa tupu au si sahihi,
  // mfumo utatumia odds ya default kwa score husika.
  const getSafeScoreOdds = (inputOdds) => {
    const defaults = cloneDefaultScoreOdds();
    const supplied = Array.isArray(inputOdds) ? inputOdds : [];

    return defaults.map((defaultItem) => {
      const savedItem = supplied.find(
        (item) => item.score === defaultItem.score
      );

      const rawOdds = savedItem?.odds;
      const parsedOdds =
        rawOdds === "" || rawOdds === null || rawOdds === undefined
          ? NaN
          : Number(rawOdds);

      if (
        Number.isFinite(parsedOdds) &&
        parsedOdds >= 1.01
      ) {
        return {
          score: defaultItem.score,
          odds: parsedOdds,
        };
      }

      return {
        score: defaultItem.score,
        odds: Number(defaultItem.odds),
      };
    });
  };

  // ------------------------------
  // FORM VALIDATION
  // ------------------------------
  const validate = (values) => {
    if (!clean(values.country)) {
      return "Weka nchi ya mechi.";
    }

    if (!clean(values.league)) {
      return "Weka ligi ya mechi.";
    }

    if (!clean(values.homeTeam)) {
      return "Weka jina la timu ya nyumbani.";
    }

    if (!clean(values.awayTeam)) {
      return "Weka jina la timu ya ugenini.";
    }

    if (
      clean(values.homeTeam).toLowerCase() ===
      clean(values.awayTeam).toLowerCase()
    ) {
      return "Timu za nyumbani na ugenini haziwezi kuwa sawa.";
    }

    if (!values.date || !validDate(values.date)) {
      return "Weka tarehe sahihi ya mechi.";
    }

    if (!values.time) {
      return "Weka muda wa mechi.";
    }

    const optionalOdds = [
      ["Home odds", values.homeOdds],
      ["Away odds", values.awayOdds],
      ["Over odds", values.overOdds],
      ["Under odds", values.underOdds],
    ];

    for (const [label, rawValue] of optionalOdds) {
      if (rawValue === "" || rawValue === null || rawValue === undefined) {
        continue;
      }

      const parsed = Number(rawValue);

      if (!Number.isFinite(parsed) || parsed < 1.01) {
        return `${label} si sahihi. Odds lazima iwe 1.01 au zaidi.`;
      }
    }

    if (
      values.totalLine !== "" &&
      values.totalLine !== null &&
      values.totalLine !== undefined
    ) {
      const total = Number(values.totalLine);

      if (!Number.isFinite(total) || total < 0) {
        return "Total Runs si sahihi.";
      }
    }

    // Hakuna validation ya scoreOdds hapa.
    // Correct Score inaweza kuwa tupu au kuwa na thamani isiyo sahihi
    // bila kuzuia mechi kuhifadhiwa.

    return "";
  };

  // ------------------------------
  // BUILD MATCH DOCUMENT
  // ------------------------------
  const buildMatch = (values) => {
    const homeTeam = clean(values.homeTeam);
    const awayTeam = clean(values.awayTeam);

    return {
      sport: "baseball",

      country: clean(values.country),
      league: clean(values.league),

      homeTeam,
      awayTeam,

      // Alternative team names for compatibility.
      home: homeTeam,
      away: awayTeam,
      A: homeTeam,
      B: awayTeam,

      date: values.date,
      time: values.time,
      timezone: TIMEZONE,

      odds: {
        home:
          values.homeOdds === "" ||
          values.homeOdds === null ||
          values.homeOdds === undefined
            ? null
            : Number(values.homeOdds),

        away:
          values.awayOdds === "" ||
          values.awayOdds === null ||
          values.awayOdds === undefined
            ? null
            : Number(values.awayOdds),
      },

      totalRuns: {
        line:
          values.totalLine === "" ||
          values.totalLine === null ||
          values.totalLine === undefined
            ? null
            : Number(values.totalLine),

        overOdds:
          values.overOdds === "" ||
          values.overOdds === null ||
          values.overOdds === undefined
            ? null
            : Number(values.overOdds),

        underOdds:
          values.underOdds === "" ||
          values.underOdds === null ||
          values.underOdds === undefined
            ? null
            : Number(values.underOdds),
      },

      // Always save a complete, safe set of Correct Score odds.
      scoreOdds: getSafeScoreOdds(values.scoreOdds),

      score: {
        home: null,
        away: null,
      },

      status: "upcoming",
      updatedAt: serverTimestamp(),
    };
  };

  // ------------------------------
  // ADD ONE MATCH
  // ------------------------------
  const addMatch = async (event) => {
    event.preventDefault();

    setError("");
    setSuccess("");

    const validationError = validate(form);

    if (validationError) {
      setError(validationError);
      return;
    }

    if (!getAppLoginUser()) {
      setError("Tafadhali ingia kama admin kabla ya kuhifadhi mechi.");
      return;
    }

    setSaving(true);

    try {
      const matchData = buildMatch(form);

      await addDoc(collection(db, COLLECTION), {
        ...matchData,
        source: "admin",
        createdBy: getAppLoginUser()?.uid || getAppLoginUser()?.username || "unknown",
        createdAt: serverTimestamp(),
      });

      setSuccess(
        "Mechi imehifadhiwa kikamilifu. Correct Score odds zimetumia default pale zilipokuwa tupu au si sahihi."
      );

      resetForm();
      await loadMatches();
    } catch (err) {
      console.error("Error adding baseball match:", err);
      setError("Imeshindikana kuhifadhi mechi. Angalia Firebase na ruhusa za admin.");
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------
  // PARSE BULK TEXT
  // ------------------------------
  const handlePreviewBulk = () => {
    setError("");
    setSuccess("");

    if (!clean(bulkText)) {
      setBulkPreview([]);
      setError("Bandika kwanza maandishi ya bookmaker.");
      return;
    }

    try {
      const parsedMatches = parseBaseballText(bulkText);

      if (!parsedMatches.length) {
        setBulkPreview([]);
        setError(
          "Hakuna mechi iliyotambuliwa. Hakikisha maandishi yana majina ya timu, odds na muda."
        );
        return;
      }

      setBulkPreview(parsedMatches);

      setSuccess(
        `Mechi ${parsedMatches.length} zimepatikana. Kagua taarifa kabla ya kuzi-import.`
      );
    } catch (err) {
      console.error("Error parsing bookmaker text:", err);
      setBulkPreview([]);
      setError("Imeshindikana kusoma maandishi ya bookmaker.");
    }
  };

  // ------------------------------
  // IMPORT MANY MATCHES
  // ------------------------------
  const importBulk = async (event) => {
    event.preventDefault();

    setError("");
    setSuccess("");

    if (!getAppLoginUser()) {
      setError("Tafadhali ingia kama admin kabla ya ku-import mechi.");
      return;
    }

    const items = bulkPreview.length
      ? bulkPreview
      : parseBaseballText(bulkText);

    if (!items.length) {
      setError("Hakuna mechi za ku-import. Bonyeza Preview kwanza.");
      return;
    }

    // Validate required match fields, but never validate Correct Score odds.
    const invalidItems = items.filter((item) => validate(item));

    if (invalidItems.length) {
      setError(
        `Kuna mechi ${invalidItems.length} zenye taarifa muhimu zisizo sahihi. Kagua majina ya timu, tarehe na muda.`
      );
      return;
    }

    setSaving(true);

    let importedCount = 0;
    const failedItems = [];

    try {
      for (const item of items) {
        try {
          const matchData = buildMatch(item);

          await addDoc(collection(db, COLLECTION), {
            ...matchData,
            source: "bookmaker-import",
            createdBy: getAppLoginUser()?.uid || getAppLoginUser()?.username || "unknown",
            createdAt: serverTimestamp(),
          });

          importedCount += 1;
        } catch (err) {
          console.error("Failed importing baseball match:", item, err);
          failedItems.push(item);
        }
      }

      await loadMatches();

      if (failedItems.length === 0) {
        setSuccess(
          `Import imekamilika. Mechi ${importedCount} zimehifadhiwa.`
        );

        setBulkText("");
        setBulkPreview([]);
      } else {
        setError(
          `Mechi ${importedCount} zimehifadhiwa; ${failedItems.length} hazikuhifadhiwa. Unaweza kujaribu tena baada ya kukagua Firebase.`
        );
      }
    } catch (err) {
      console.error("Bulk import error:", err);
      setError("Import imeshindwa kukamilika.");
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------
  // SELECT MATCH FOR EDITING
  // ------------------------------
  const selectMatch = (match) => {
    const totalRuns = match.totalRuns || {};
    const savedOdds = match.odds || {};

    setSelectedId(match.id);

    setForm({
      country: match.country || EMPTY_FORM.country,
      league: match.league || EMPTY_FORM.league,

      homeTeam:
        match.homeTeam || match.home || match.A || "",
      awayTeam:
        match.awayTeam || match.away || match.B || "",

      date: match.date || "",
      time: match.time || "",

      homeOdds:
        savedOdds.home === null || savedOdds.home === undefined
          ? ""
          : String(savedOdds.home),

      awayOdds:
        savedOdds.away === null || savedOdds.away === undefined
          ? ""
          : String(savedOdds.away),

      totalLine:
        totalRuns.line === null || totalRuns.line === undefined
          ? EMPTY_FORM.totalLine
          : String(totalRuns.line),

      overOdds:
        totalRuns.overOdds === null ||
        totalRuns.overOdds === undefined
          ? ""
          : String(totalRuns.overOdds),

      underOdds:
        totalRuns.underOdds === null ||
        totalRuns.underOdds === undefined
          ? ""
          : String(totalRuns.underOdds),

      scoreOdds: normaliseScoreOdds(match.scoreOdds),
    });

    setError("");
    setSuccess("");

    setTimeout(() => {
      document
        .getElementById("baseball-form")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  // ------------------------------
  // SAVE EDITED MATCH
  // ------------------------------
  const saveEdit = async (event) => {
    event.preventDefault();

    setError("");
    setSuccess("");

    if (!selectedId) {
      setError("Chagua mechi unayotaka kuhariri.");
      return;
    }

    const validationError = validate(form);

    if (validationError) {
      setError(validationError);
      return;
    }

    if (!getAppLoginUser()) {
      setError("Tafadhali ingia kama admin kabla ya kuhifadhi mabadiliko.");
      return;
    }

    setSaving(true);

    try {
      const matchData = buildMatch(form);

      await updateDoc(doc(db, COLLECTION, selectedId), {
        ...matchData,
        updatedBy: getAppLoginUser()?.uid || getAppLoginUser()?.username || "unknown",
      });

      setSuccess(
        "Mabadiliko yamehifadhiwa. Correct Score odds tupu au zisizo sahihi zimetumia default."
      );

      resetForm();
      await loadMatches();
    } catch (err) {
      console.error("Error updating baseball match:", err);
      setError("Imeshindikana kuhifadhi mabadiliko ya mechi.");
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------
  // SEARCH MATCHES
  // ------------------------------
  const filteredMatches = useMemo(() => {
    const query = clean(search).toLowerCase();

    if (!query) return matches;

    return matches.filter((match) => {
      const searchableText = [
        match.country,
        match.league,
        match.homeTeam,
        match.awayTeam,
        match.home,
        match.away,
        match.A,
        match.B,
        match.date,
        match.time,
        match.status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(query);
    });
  }, [matches, search]);

  // SEHEMU YA 3 ITAANZA HAPA:
  // return (...) ya UI ya React.
    // ==============================
  // SEHEMU YA 3: RETURN / UI
  // ==============================

  return (
    <main className="admin-baseball">
      <header className="baseball-header">
        <div>
          <span className="baseball-eyebrow">ANTBET / ADMIN</span>

          <h1>Admin Add Baseball</h1>

          <p>
            Ongeza mechi za baseball, import bookmaker text,
            na hariri odds za mechi zilizohifadhiwa.
          </p>
        </div>

        <div className="baseball-count">
          <strong>{matches.length}</strong>
          <span>Mechi</span>
        </div>
      </header>

      {error && (
        <div className="baseball-alert error" role="alert">
          {error}
        </div>
      )}

      {success && (
        <div className="baseball-alert success" role="status">
          {success}
        </div>
      )}

      {/* =========================
          BOOKMAKER BULK IMPORT
      ========================= */}
      <section className="baseball-panel">
        <div className="baseball-section-heading">
          <div>
            <h2>Import Bookmaker Matches</h2>
            <p>
              Bandika maandishi ya bookmaker hapa chini,
              kisha preview kabla ya kuhifadhi mechi.
            </p>
          </div>
        </div>

        <form onSubmit={importBulk}>
          <label
            className="baseball-label"
            htmlFor="baseball-bulk-text"
          >
            Bookmaker text
          </label>

          <textarea
            id="baseball-bulk-text"
            className="baseball-textarea"
            rows={12}
            value={bulkText}
            onChange={(event) => {
              setBulkText(event.target.value);
              setBulkPreview([]);
              setError("");
              setSuccess("");
            }}
            placeholder={`Mfano:

Cuba - Serie Nacional

| Home | Away | Odds |
| Cachorros de Holguin | Avispas de Santiago de Cuba | 1.83 1.83 |

Total Runs 10.5
Under 1.84
Over 1.83

Today 20:30`}
          />

          <div className="baseball-actions">
            <button
              type="button"
              className="baseball-btn secondary"
              onClick={handlePreviewBulk}
              disabled={saving}
            >
              Preview Matches
            </button>

            <button
              type="submit"
              className="baseball-btn primary"
              disabled={saving}
            >
              {saving
                ? "Ina-import..."
                : `Import ${bulkPreview.length || ""} Matches`}
            </button>

            <button
              type="button"
              className="baseball-btn secondary"
              onClick={() => {
                setBulkText("");
                setBulkPreview([]);
                setError("");
                setSuccess("");
              }}
              disabled={saving}
            >
              Clear
            </button>
          </div>
        </form>

        {bulkPreview.length > 0 && (
          <div className="baseball-preview">
            <h3>
              Preview ({bulkPreview.length}{" "}
              {bulkPreview.length === 1 ? "match" : "matches"})
            </h3>

            <p className="baseball-muted">
              Hakikisha timu, tarehe, muda na odds ni sahihi
              kabla ya kubonyeza Import.
            </p>

            <div className="baseball-table-wrap">
              <table className="baseball-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Home Team</th>
                    <th>Away Team</th>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Winner Odds</th>
                    <th>Total Runs</th>
                  </tr>
                </thead>

                <tbody>
                  {bulkPreview.map((item, index) => (
                    <tr key={`${item.homeTeam}-${item.awayTeam}-${index}`}>
                      <td>{index + 1}</td>
                      <td>{item.homeTeam || "—"}</td>
                      <td>{item.awayTeam || "—"}</td>
                      <td>{item.date || "—"}</td>
                      <td>{item.time || "—"}</td>
                      <td>
                        {item.homeOdds || "—"} /{" "}
                        {item.awayOdds || "—"}
                      </td>
                      <td>
                        {item.totalLine || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* =========================
          ADD / EDIT MATCH FORM
      ========================= */}
      <section
        className="baseball-panel"
        id="baseball-form"
      >
        <div className="baseball-section-heading">
          <div>
            <h2>
              {selectedId
                ? "Hariri Mechi"
                : "Ongeza Mechi Moja"}
            </h2>

            <p>
              {selectedId
                ? "Badilisha taarifa na odds za mechi uliyochagua."
                : "Jaza taarifa za mechi mpya ya baseball."}
            </p>
          </div>

          {selectedId && (
            <span className="baseball-edit-badge">
              Editing
            </span>
          )}
        </div>

        <form
          onSubmit={selectedId ? saveEdit : addMatch}
        >
          {/* BASIC MATCH INFORMATION */}
          <div className="baseball-form-grid">
            <div className="baseball-field">
              <label
                className="baseball-label"
                htmlFor="baseball-country"
              >
                Country
              </label>

              <input
                id="baseball-country"
                className="baseball-input"
                type="text"
                name="country"
                value={form.country}
                onChange={changeForm}
                placeholder="Cuba"
                required
              />
            </div>

            <div className="baseball-field">
              <label
                className="baseball-label"
                htmlFor="baseball-league"
              >
                League
              </label>

              <input
                id="baseball-league"
                className="baseball-input"
                type="text"
                name="league"
                value={form.league}
                onChange={changeForm}
                placeholder="Serie Nacional"
                required
              />
            </div>

            <div className="baseball-field">
              <label
                className="baseball-label"
                htmlFor="baseball-home-team"
              >
                Home Team
              </label>

              <input
                id="baseball-home-team"
                className="baseball-input"
                type="text"
                name="homeTeam"
                value={form.homeTeam}
                onChange={changeForm}
                placeholder="Cachorros de Holguin"
                required
              />
            </div>

            <div className="baseball-field">
              <label
                className="baseball-label"
                htmlFor="baseball-away-team"
              >
                Away Team
              </label>

              <input
                id="baseball-away-team"
                className="baseball-input"
                type="text"
                name="awayTeam"
                value={form.awayTeam}
                onChange={changeForm}
                placeholder="Avispas de Santiago de Cuba"
                required
              />
            </div>

            <div className="baseball-field">
              <label
                className="baseball-label"
                htmlFor="baseball-date"
              >
                Match Date
              </label>

              <input
                id="baseball-date"
                className="baseball-input"
                type="date"
                name="date"
                value={form.date}
                onChange={changeForm}
                required
              />
            </div>

            <div className="baseball-field">
              <label
                className="baseball-label"
                htmlFor="baseball-time"
              >
                Match Time
              </label>

              <input
                id="baseball-time"
                className="baseball-input"
                type="time"
                name="time"
                value={form.time}
                onChange={changeForm}
                required
              />

              <small className="baseball-muted">
                Timezone: {TIMEZONE}
              </small>
            </div>
          </div>

          {/* MATCH WINNER ODDS */}
          <div className="baseball-subsection">
            <h3>Match Winner Odds</h3>

            <div className="baseball-form-grid">
              <div className="baseball-field">
                <label
                  className="baseball-label"
                  htmlFor="baseball-home-odds"
                >
                  Home Win Odds
                </label>

                <input
                  id="baseball-home-odds"
                  className="baseball-input"
                  type="number"
                  name="homeOdds"
                  value={form.homeOdds}
                  onChange={changeForm}
                  min="1.01"
                  step="0.01"
                  placeholder="1.83"
                />
              </div>

              <div className="baseball-field">
                <label
                  className="baseball-label"
                  htmlFor="baseball-away-odds"
                >
                  Away Win Odds
                </label>

                <input
                  id="baseball-away-odds"
                  className="baseball-input"
                  type="number"
                  name="awayOdds"
                  value={form.awayOdds}
                  onChange={changeForm}
                  min="1.01"
                  step="0.01"
                  placeholder="1.83"
                />
              </div>
            </div>
          </div>

          {/* TOTAL RUNS ODDS */}
          <div className="baseball-subsection">
            <h3>Total Runs</h3>

            <div className="baseball-form-grid">
              <div className="baseball-field">
                <label
                  className="baseball-label"
                  htmlFor="baseball-total-line"
                >
                  Total Runs Line
                </label>

                <input
                  id="baseball-total-line"
                  className="baseball-input"
                  type="number"
                  name="totalLine"
                  value={form.totalLine}
                  onChange={changeForm}
                  min="0"
                  step="0.5"
                  placeholder="10.5"
                />
              </div>

              <div className="baseball-field">
                <label
                  className="baseball-label"
                  htmlFor="baseball-over-odds"
                >
                  Over Odds
                </label>

                <input
                  id="baseball-over-odds"
                  className="baseball-input"
                  type="number"
                  name="overOdds"
                  value={form.overOdds}
                  onChange={changeForm}
                  min="1.01"
                  step="0.01"
                  placeholder="1.83"
                />
              </div>

              <div className="baseball-field">
                <label
                  className="baseball-label"
                  htmlFor="baseball-under-odds"
                >
                  Under Odds
                </label>

                <input
                  id="baseball-under-odds"
                  className="baseball-input"
                  type="number"
                  name="underOdds"
                  value={form.underOdds}
                  onChange={changeForm}
                  min="1.01"
                  step="0.01"
                  placeholder="1.84"
                />
              </div>
            </div>
          </div>

          {/* CORRECT SCORE ODDS */}
          <div className="baseball-subsection">
            <div className="baseball-section-heading">
              <div>
                <h3>Correct Score Odds</h3>

                <p className="baseball-muted">
                  Odds hizi zinaweza kuachwa wazi. Mfumo
                  utatumia default odds kwa score yoyote
                  yenye thamani tupu au isiyo sahihi.
                </p>
              </div>

              <button
                type="button"
                className="baseball-btn secondary"
                onClick={() => {
                  setForm((previous) => ({
                    ...previous,
                    scoreOdds: cloneDefaultScoreOdds(),
                  }));
                }}
              >
                Reset Defaults
              </button>
            </div>

            <div className="baseball-score-grid">
              {(form.scoreOdds || cloneDefaultScoreOdds()).map(
                (item) => (
                  <div
                    className="baseball-score-item"
                    key={item.score}
                  >
                    <label
                      className="baseball-label"
                      htmlFor={`baseball-score-${item.score
                        .replace(/\s+/g, "-")
                        .toLowerCase()}`}
                    >
                      {item.score}
                    </label>

                    <input
                      id={`baseball-score-${item.score
                        .replace(/\s+/g, "-")
                        .toLowerCase()}`}
                      className="baseball-input"
                      type="number"
                      min="1.01"
                      step="0.01"
                      value={item.odds ?? ""}
                      onChange={(event) =>
                        changeScoreOdd(
                          item.score,
                          event.target.value
                        )
                      }
                      placeholder="Default"
                    />
                  </div>
                )
              )}
            </div>
          </div>

          {/* FORM BUTTONS */}
          <div className="baseball-actions">
            <button
              type="submit"
              className="baseball-btn primary"
              disabled={saving}
            >
              {saving
                ? "Inahifadhi..."
                : selectedId
                  ? "Save Changes"
                  : "Add Baseball Match"}
            </button>

            <button
              type="button"
              className="baseball-btn secondary"
              onClick={resetForm}
              disabled={saving}
            >
              {selectedId ? "Cancel Editing" : "Clear Form"}
            </button>
          </div>
        </form>
      </section>

      {/* SEHEMU YA 4:
          LIST YA MECHI ZILIZOHIFADHIWA,
          SEARCH, NA BUTTON YA KUHARIRI
      */}
            {/* =========================
          SAVED BASEBALL MATCHES
      ========================= */}
      <section className="baseball-panel">
        <div className="baseball-section-heading">
          <div>
            <h2>Mechi Zilizohifadhiwa</h2>
            <p>
              Tafuta mechi, angalia odds zake, au chagua
              mechi ili kuhariri taarifa zake.
            </p>
          </div>

          <button
            type="button"
            className="baseball-btn secondary"
            onClick={loadMatches}
            disabled={loading}
          >
            {loading ? "Inapakia..." : "Refresh"}
          </button>
        </div>

        <div className="baseball-search">
          <label
            className="baseball-label"
            htmlFor="baseball-search"
          >
            Tafuta mechi
          </label>

          <input
            id="baseball-search"
            className="baseball-input"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Tafuta timu, ligi, nchi au tarehe..."
          />
        </div>

        <div className="baseball-list-summary">
          <span>
            Zinaonyeshwa: <strong>{filteredMatches.length}</strong>
          </span>

          <span>
            Jumla: <strong>{matches.length}</strong>
          </span>
        </div>

        {loading && matches.length === 0 ? (
          <div className="baseball-empty">
            Inapakia mechi kutoka Firebase...
          </div>
        ) : filteredMatches.length === 0 ? (
          <div className="baseball-empty">
            {matches.length === 0
              ? "Hakuna mechi zilizohifadhiwa kwa sasa."
              : "Hakuna mechi inayolingana na utafutaji wako."}
          </div>
        ) : (
          <div className="baseball-match-list">
            {filteredMatches.map((match) => {
              const homeTeam =
                match.homeTeam || match.home || match.A || "Home";

              const awayTeam =
                match.awayTeam || match.away || match.B || "Away";

              const winnerOdds = match.odds || {};
              const totalRuns = match.totalRuns || {};

              const isSelected = selectedId === match.id;

              return (
                <article
                  className={`baseball-match-card ${
                    isSelected ? "selected" : ""
                  }`}
                  key={match.id}
                >
                  <div className="baseball-match-top">
                    <div className="baseball-match-league">
                      <span>{match.country || "Baseball"}</span>
                      <span>·</span>
                      <span>{match.league || "League"}</span>
                    </div>

                    <span
                      className={`baseball-status ${
                        String(match.status || "upcoming").toLowerCase()
                      }`}
                    >
                      {match.status || "upcoming"}
                    </span>
                  </div>

                  <div className="baseball-match-teams">
                    <div className="baseball-team">
                      <span className="baseball-team-label">
                        HOME
                      </span>
                      <strong>{homeTeam}</strong>
                    </div>

                    <span className="baseball-vs">VS</span>

                    <div className="baseball-team away">
                      <span className="baseball-team-label">
                        AWAY
                      </span>
                      <strong>{awayTeam}</strong>
                    </div>
                  </div>

                  <div className="baseball-match-meta">
                    <span>
                      <strong>Tarehe:</strong>{" "}
                      {match.date || "—"}
                    </span>

                    <span>
                      <strong>Muda:</strong>{" "}
                      {match.time || "—"}
                    </span>

                    <span>
                      <strong>Timezone:</strong>{" "}
                      {match.timezone || TIMEZONE}
                    </span>
                  </div>

                  <div className="baseball-match-odds">
                    <div className="baseball-odd-box">
                      <span>Home Win</span>
                      <strong>
                        {winnerOdds.home ?? "—"}
                      </strong>
                    </div>

                    <div className="baseball-odd-box">
                      <span>Away Win</span>
                      <strong>
                        {winnerOdds.away ?? "—"}
                      </strong>
                    </div>

                    <div className="baseball-odd-box">
                      <span>Total Runs</span>
                      <strong>
                        {totalRuns.line ?? "—"}
                      </strong>
                    </div>

                    <div className="baseball-odd-box">
                      <span>Over</span>
                      <strong>
                        {totalRuns.overOdds ?? "—"}
                      </strong>
                    </div>

                    <div className="baseball-odd-box">
                      <span>Under</span>
                      <strong>
                        {totalRuns.underOdds ?? "—"}
                      </strong>
                    </div>
                  </div>

                  <div className="baseball-match-footer">
                    <span className="baseball-match-id">
                      ID: {match.id}
                    </span>

                    <button
                      type="button"
                      className="baseball-btn primary"
                      onClick={() => selectMatch(match)}
                    >
                      {isSelected
                        ? "Mechi Imechaguliwa"
                        : "Hariri Mechi"}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}